import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Search,
  Package,
  CheckCircle,
  Truck,
  MapPin,
  ExternalLink,
  Copy,
  ArrowRight,
  ShieldCheck,
  ShoppingBag,
  User,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.vanyacollectionsrajahmundry.com";

const TrackOrderPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ordersList, setOrdersList] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [copiedTracking, setCopiedTracking] = useState(false);
  const [searchType, setSearchType] = useState(""); // "account", "phone", "order", "account_empty"

  const formatOrderCode = (ord) => {
    if (!ord) return "";
    if (typeof ord === "object") {
      if (ord.order_number) return ord.order_number;
      if (ord.id) return `VAN-${String(ord.id).padStart(2, "0")}`;
    }
    const str = String(ord).trim();
    if (str.toUpperCase().startsWith("VAN-")) return str.toUpperCase();
    const cleanNum = str.replace(/\D/g, "");
    return cleanNum ? `VAN-${cleanNum.padStart(2, "0")}` : str;
  };

  // Search orders specifically by Query (Order ID, phone, etc.)
  const fetchOrderByIdOrPhone = useCallback(async (queryToSearch) => {
    const q = (queryToSearch || "").trim();
    if (!q) {
      setError("Please enter an Order ID or Phone Number");
      return;
    }

    setLoading(true);
    setError("");
    setOrdersList([]);

    try {
      const res = await fetch(
        `${API_BASE}/orders/track/search?query=${encodeURIComponent(q)}`
      );
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to search order");
      }

      setSearchType(data.searchType || "manual");

      const foundOrders = data.orders || [];
      if (foundOrders.length === 0) {
        setError(`No orders found matching "${q}". Please double check your details.`);
        setSelectedOrder(null);
        setOrdersList([]);
      } else if (foundOrders.length === 1) {
        setSelectedOrder(foundOrders[0]);
        setOrdersList(foundOrders);
      } else {
        setOrdersList(foundOrders);
        setSelectedOrder(foundOrders[0]);
      }
    } catch (err) {
      console.error("Tracking search error:", err);
      setError(err.message || "Network error. Please try again.");
      setSelectedOrder(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch orders specifically for the logged-in user account
  const fetchLoggedInUserOrders = useCallback(async (user, targetOrderId = null) => {
    if (!user) return;
    setLoading(true);
    setError("");

    try {
      const userId = user.id || user.userId || "";
      const userPhone = (user.phone || "").replace(/\D/g, "").slice(-10);
      const userEmail = (user.email || "").trim().toLowerCase();

      let orders = [];

      // 1. Fetch by user ID (which also checks phone & email on backend)
      const res = await fetch(
        `${API_BASE}/orders/user/${userId}?phone=${encodeURIComponent(userPhone)}&email=${encodeURIComponent(userEmail)}`
      );

      if (res.ok) {
        const data = await res.json();
        orders = Array.isArray(data) ? data : data.orders || [];
      }

      // 2. If no orders found and phone exists, fallback search by phone
      if (orders.length === 0 && userPhone) {
        const phoneRes = await fetch(
          `${API_BASE}/orders/track/search?query=${encodeURIComponent(userPhone)}`
        );
        const phoneData = await phoneRes.json();
        if (phoneRes.ok && phoneData.success && Array.isArray(phoneData.orders)) {
          orders = phoneData.orders;
        }
      }

      if (orders.length > 0) {
        setOrdersList(orders);
        setSearchType("account");

        if (targetOrderId) {
          const matched = orders.find(
            (o) =>
              formatOrderCode(o) === formatOrderCode(targetOrderId) ||
              String(o.id) === String(targetOrderId)
          );
          setSelectedOrder(matched || orders[0]);
        } else {
          setSelectedOrder(orders[0]); // default to latest order
        }
      } else {
        setOrdersList([]);
        setSelectedOrder(null);
        setSearchType("account_empty");
      }
    } catch (err) {
      console.error("Account orders fetch error:", err);
      setError("Could not automatically load account orders. Please enter your order number or phone below.");
      setSelectedOrder(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check URL params or location state for initial search OR logged in user
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderIdParam = params.get("order_id") || location.state?.orderId;
    const phoneParam = params.get("phone");

    // Read latest user from localStorage
    let user = null;
    try {
      const stored = localStorage.getItem("user");
      user = stored ? JSON.parse(stored) : null;
      setCurrentUser(user);
    } catch (e) {
      user = null;
    }

    if (orderIdParam) {
      const formatted = formatOrderCode(orderIdParam.toString());
      setSearchQuery(formatted);
      if (user && (user.id || user.phone || user.email)) {
        fetchLoggedInUserOrders(user, formatted);
      } else {
        fetchOrderByIdOrPhone(formatted);
      }
    } else if (phoneParam) {
      setSearchQuery(phoneParam);
      fetchOrderByIdOrPhone(phoneParam);
    } else if (user && (user.id || user.phone || user.email)) {
      // Automatic loading for logged-in user!
      fetchLoggedInUserOrders(user);
    }
  }, [location, fetchOrderByIdOrPhone, fetchLoggedInUserOrders]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    let queryToSearch = searchQuery.trim();

    if (!queryToSearch) {
      setError("Please enter an Order ID or 10-digit Phone Number");
      return;
    }

    // If user enters a short number (< 10 digits, e.g. "07" or "7"), auto-format as "VAN-07"
    if (/^\d+$/.test(queryToSearch) && queryToSearch.length < 10) {
      queryToSearch = formatOrderCode(queryToSearch);
      setSearchQuery(queryToSearch);
    }

    fetchOrderByIdOrPhone(queryToSearch);
  };

  const handleCopyTracking = (awb) => {
    if (!awb) return;
    navigator.clipboard.writeText(awb);
    setCopiedTracking(true);
    setTimeout(() => setCopiedTracking(false), 2000);
  };

  // Determine active step index:
  // 0: Order Placed
  // 1: Confirmed
  // 2: Packed
  // 3: Shipped
  // 4: Delivered
  const getStepStatus = (orderStatus) => {
    const status = (orderStatus || "").toLowerCase();
    if (status === "delivered") return 4;
    if (status === "shipped") return 3;
    if (status === "packed") return 2;
    if (status === "confirmed") return 1;
    if (status === "cancelled" || status === "payment_rejected") return -1;
    return 0; // pending / payment_pending
  };

  const isJustPlaced =
    new URLSearchParams(location.search).get("placed") === "true" ||
    location.state?.isNewOrder;

  return (
    <div className="track-order-page">
      <style>{`
        .track-order-page {
          background-color: #fcf9fb;
          min-height: 90vh;
          padding: 40px 20px 80px;
          font-family: 'Playfair Display', serif;
          color: #2c2c2c;
        }

        .track-container {
          max-width: 960px;
          margin: 0 auto;
        }

        .track-hero {
          text-align: center;
          margin-bottom: 30px;
        }

        .track-badge {
          display: inline-block;
          font-size: 11px;
          letter-spacing: 2px;
          font-weight: 700;
          color: #8c5b1b;
          background: #fbf3df;
          padding: 6px 16px;
          border-radius: 20px;
          text-transform: uppercase;
          margin-bottom: 12px;
        }

        .track-title {
          font-size: 2.2rem;
          color: #522b5b;
          font-weight: 700;
          margin-bottom: 10px;
        }

        .track-subtitle {
          color: #666;
          font-size: 0.95rem;
          max-width: 550px;
          margin: 0 auto;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Logged In Account Banner */
        .account-orders-banner {
          background: linear-gradient(135deg, #f7f1fb 0%, #fdf8ee 100%);
          border: 1.5px solid #d4af37;
          border-radius: 16px;
          padding: 16px 20px;
          margin-bottom: 25px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 14px;
          box-shadow: 0 6px 20px rgba(82, 43, 91, 0.05);
        }

        .account-orders-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .account-avatar-icon {
          width: 44px;
          height: 44px;
          background: #522b5b;
          color: #d4af37;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .account-orders-title {
          font-weight: 700;
          color: #522b5b;
          font-size: 1.05rem;
          margin: 0 0 3px;
        }

        .account-orders-sub {
          font-size: 0.85rem;
          color: #666;
          margin: 0;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .account-orders-count {
          background: #522b5b;
          color: #d4af37;
          font-weight: 700;
          font-size: 0.82rem;
          padding: 6px 14px;
          border-radius: 20px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          letter-spacing: 0.5px;
        }

        /* Success Alert */
        .order-placed-banner {
          background: linear-gradient(135deg, #183B32 0%, #063b2a 100%);
          color: #ffffff;
          padding: 20px 24px;
          border-radius: 14px;
          margin-bottom: 30px;
          box-shadow: 0 10px 25px rgba(6, 59, 42, 0.2);
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .order-placed-banner h3 {
          margin: 0 0 4px;
          font-size: 1.25rem;
          color: #fbd349;
        }

        .order-placed-banner p {
          margin: 0;
          font-size: 0.88rem;
          color: #e2f1ec;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Search Card */
        .track-search-card {
          background: #ffffff;
          border: 1px solid #ebdfee;
          border-radius: 16px;
          padding: 24px 28px;
          box-shadow: 0 8px 30px rgba(82, 43, 91, 0.05);
          margin-bottom: 35px;
        }

        .track-search-form {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .track-input-wrapper {
          position: relative;
          flex: 1;
        }

        .track-input-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #8c7295;
        }

        .track-input {
          width: 100%;
          box-sizing: border-box;
          padding: 14px 16px 14px 48px;
          font-size: 0.95rem;
          border: 1.5px solid #e1d3e6;
          border-radius: 10px;
          outline: none;
          transition: all 0.2s ease;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .track-input:focus {
          border-color: #633878;
          box-shadow: 0 0 0 3px rgba(99, 56, 120, 0.12);
        }

        .track-submit-btn {
          background: #633878;
          color: #ffffff;
          border: none;
          border-radius: 10px;
          padding: 14px 26px;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: background 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .track-submit-btn:hover {
          background: #502b62;
        }

        .track-error-msg {
          margin-top: 14px;
          color: #c53030;
          background: #fff5f5;
          border: 1px solid #feb2b2;
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 0.88rem;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Empty Account Orders Card */
        .empty-orders-card {
          background: #ffffff;
          border: 1.5px dashed #d5c0df;
          border-radius: 20px;
          padding: 50px 30px;
          text-align: center;
          margin-bottom: 35px;
        }

        .empty-orders-card h3 {
          color: #522b5b;
          font-size: 1.5rem;
          margin: 15px 0 8px;
        }

        .empty-orders-card p {
          color: #666;
          font-size: 0.95rem;
          max-width: 480px;
          margin: 0 auto 20px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Orders Selector Tabs */
        .orders-tabs-wrapper {
          margin-bottom: 25px;
        }

        .orders-tabs-label {
          font-size: 0.85rem;
          font-weight: 700;
          color: #633878;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .orders-tabs-list {
          display: flex;
          gap: 12px;
          overflow-x: auto;
          padding-bottom: 8px;
        }

        .order-tab-card {
          background: #ffffff;
          border: 1.5px solid #ebdfee;
          border-radius: 14px;
          padding: 14px 18px;
          cursor: pointer;
          min-width: 220px;
          transition: all 0.2s ease;
          text-align: left;
          flex-shrink: 0;
        }

        .order-tab-card.active {
          border-color: #633878;
          background: #faf4fc;
          box-shadow: 0 6px 18px rgba(99, 56, 120, 0.12);
        }

        .order-tab-id {
          font-weight: 700;
          color: #522b5b;
          font-size: 1rem;
          margin-bottom: 4px;
        }

        .order-tab-meta {
          font-size: 0.78rem;
          color: #777;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Order Details Card */
        .order-detail-card {
          background: #ffffff;
          border: 1px solid #ebdfee;
          border-radius: 18px;
          padding: 30px;
          box-shadow: 0 10px 35px rgba(82, 43, 91, 0.06);
          margin-bottom: 30px;
        }

        .order-header-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 16px;
          padding-bottom: 24px;
          border-bottom: 1px solid #f0e6f5;
        }

        .order-header-id {
          font-size: 1.6rem;
          color: #522b5b;
          font-weight: 700;
          margin: 0 0 6px;
        }

        .order-header-date {
          color: #777;
          font-size: 0.85rem;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .status-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 0.82rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .status-pill.confirmed { background: #e6f4ea; color: #137333; }
        .status-pill.packed { background: #e8f0fe; color: #1a73e8; }
        .status-pill.shipped { background: #fef7e0; color: #b06000; }
        .status-pill.delivered { background: #e6f4ea; color: #0d652d; border: 1px solid #ceead6; }
        .status-pill.pending { background: #fef0f0; color: #c53030; }

        /* Progress Stepper */
        .tracking-stepper {
          margin: 40px 0 35px;
          position: relative;
        }

        .stepper-track-bg {
          position: absolute;
          top: 24px;
          left: 5%;
          right: 5%;
          height: 4px;
          background: #eee;
          z-index: 1;
        }

        .stepper-track-fill {
          height: 100%;
          background: linear-gradient(90deg, #633878 0%, #183B32 100%);
          transition: width 0.4s ease;
        }

        .stepper-steps {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          position: relative;
          z-index: 2;
        }

        .step-node {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .step-circle {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #ffffff;
          border: 3px solid #ddd;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #aaa;
          font-size: 1rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
        }

        .step-node.completed .step-circle {
          background: #633878;
          border-color: #633878;
          color: #ffffff;
        }

        .step-node.active .step-circle {
          background: #183B32;
          border-color: #fbd349;
          color: #ffffff;
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(24, 59, 50, 0.25);
        }

        .step-label {
          margin-top: 12px;
          font-size: 0.85rem;
          font-weight: 600;
          color: #555;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .step-node.completed .step-label,
        .step-node.active .step-label {
          color: #222;
          font-weight: 700;
        }

        /* Courier Tracking Banner */
        .courier-info-card {
          background: linear-gradient(135deg, #fdfbf7 0%, #f7f1e7 100%);
          border: 1.5px solid #e3d2be;
          border-radius: 14px;
          padding: 20px 24px;
          margin-bottom: 28px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }

        .courier-info-left h4 {
          margin: 0 0 6px;
          color: #633878;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .courier-info-details {
          font-size: 0.9rem;
          color: #444;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .awb-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #ffffff;
          border: 1px solid #d5c4af;
          border-radius: 6px;
          padding: 4px 10px;
          font-weight: 700;
          font-family: monospace;
          margin: 0 6px;
          cursor: pointer;
        }

        .courier-btn {
          background: #183B32;
          color: #ffffff;
          text-decoration: none;
          padding: 10px 18px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.88rem;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: background 0.2s ease;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .courier-btn:hover {
          background: #0f2721;
        }

        /* Info Grid */
        .order-info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .info-card {
          background: #faf7fb;
          border: 1px solid #ebdfee;
          border-radius: 12px;
          padding: 18px 20px;
        }

        .info-card-title {
          font-size: 0.78rem;
          text-transform: uppercase;
          color: #7a5d85;
          letter-spacing: 1px;
          font-weight: 700;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .info-card-text {
          font-size: 0.92rem;
          color: #222;
          line-height: 1.5;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Items Table */
        .items-section-title {
          font-size: 1.15rem;
          color: #522b5b;
          margin: 0 0 16px;
          font-weight: 700;
        }

        .items-list {
          border: 1px solid #ebdfee;
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 24px;
        }

        .item-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 18px;
          border-bottom: 1px solid #f2e9f5;
          background: #ffffff;
        }

        .item-row:last-child {
          border-bottom: none;
        }

        .item-main {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .item-thumb {
          width: 55px;
          height: 70px;
          object-fit: cover;
          border-radius: 6px;
          border: 1px solid #ebdfee;
        }

        .item-name {
          font-weight: 700;
          color: #333;
          font-size: 0.95rem;
          margin-bottom: 4px;
        }

        .item-variant {
          font-size: 0.8rem;
          color: #777;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .item-price-block {
          text-align: right;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .item-price {
          font-size: 0.95rem;
          font-weight: 700;
          color: #522b5b;
        }

        .item-qty {
          font-size: 0.78rem;
          color: #888;
        }

        /* Financial summary */
        .pricing-summary {
          background: #faf7fb;
          border: 1px solid #ebdfee;
          border-radius: 12px;
          padding: 18px 22px;
          max-width: 380px;
          margin-left: auto;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .summary-line {
          display: flex;
          justify-content: space-between;
          font-size: 0.9rem;
          color: #555;
          margin-bottom: 8px;
        }

        .summary-line.total {
          border-top: 1.5px solid #ebdfee;
          padding-top: 12px;
          margin-top: 10px;
          font-size: 1.15rem;
          font-weight: 700;
          color: #522b5b;
        }

        @media (max-width: 768px) {
          .track-search-form {
            flex-direction: column;
          }
          .track-submit-btn {
            width: 100%;
            justify-content: center;
          }
          .stepper-steps {
            gap: 4px;
          }
          .step-circle {
            width: 36px;
            height: 36px;
            font-size: 0.8rem;
          }
          .step-label {
            font-size: 0.7rem;
          }
          .pricing-summary {
            max-width: 100%;
          }
        }
      `}</style>

      <div className="track-container">
        {/* HERO TITLE */}
        <div className="track-hero">
          <div className="track-badge">Royal Consignment Tracking</div>
          <h1 className="track-title">Track Your Saree Order</h1>
          <p className="track-subtitle">
            {currentUser
              ? `Welcome back, ${currentUser.fullName || currentUser.name || "valued customer"}. Inspect live parcel status and delivery progress.`
              : "Enter your 10-digit mobile number or Order ID to inspect live progress, courier status, and consignment details."}
          </p>
        </div>

        {/* LOGGED IN ACCOUNT BANNER */}
        {currentUser && (
          <div className="account-orders-banner">
            <div className="account-orders-info">
              <div className="account-avatar-icon">
                {currentUser.fullName ? currentUser.fullName.charAt(0).toUpperCase() : <User size={22} />}
              </div>
              <div>
                <h3 className="account-orders-title">
                  Orders for {currentUser.fullName || currentUser.name || "My Account"}
                </h3>
                <p className="account-orders-sub">
                  {currentUser.phone && (
                    <strong style={{ color: "#522b5b" }}>📱 +91 {currentUser.phone.replace(/\D/g, "").slice(-10)} • </strong>
                  )}
                  <span>✉️ {currentUser.email}</span>
                </p>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {ordersList.length > 0 && (
                <span className="account-orders-count">
                  {ordersList.length} Order{ordersList.length > 1 ? "s" : ""} Found
                </span>
              )}
              {searchType !== "account" && ordersList.length > 0 && (
                <button
                  onClick={() => fetchLoggedInUserOrders(currentUser)}
                  style={{
                    background: "#522b5b",
                    color: "#fff",
                    border: "none",
                    padding: "6px 14px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  ↻ View All My Orders
                </button>
              )}
            </div>
          </div>
        )}

        {/* JUST PLACED SUCCESS BANNER */}
        {isJustPlaced && selectedOrder && (
          <div className="order-placed-banner">
            <CheckCircle size={36} color="#fbd349" />
            <div>
              <h3>Order #{formatOrderCode(selectedOrder)} Placed Successfully!</h3>
              <p>
                Thank you for choosing Vanya Sarees. Your royal parcel is being prepared. You can track this order anytime using your phone number <strong>{selectedOrder.phone}</strong>.
              </p>
            </div>
          </div>
        )}

        {/* SEARCH BOX (Available for searching other orders or for guest users) */}
        <div className="track-search-card">
          <form className="track-search-form" onSubmit={handleSearchSubmit}>
            <div className="track-input-wrapper">
              <Search size={20} className="track-input-icon" />
              <input
                type="text"
                className="track-input"
                placeholder="Enter 10-digit Phone Number (e.g. 9876543210) or Order ID (e.g. VAN-05)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button type="submit" className="track-submit-btn" disabled={loading}>
              {loading ? "Searching..." : "Track Order"}
              <ArrowRight size={16} />
            </button>
          </form>

          {error && <div className="track-error-msg">{error}</div>}
        </div>

        {/* EMPTY ACCOUNT ORDERS STATE */}
        {searchType === "account_empty" && !loading && (
          <div className="empty-orders-card">
            <div style={{ fontSize: "48px" }}>🛍️</div>
            <h3>No Orders Placed Yet</h3>
            <p>
              You haven't placed any orders with this account ({currentUser?.email || currentUser?.phone}) yet.
              Discover our exquisite handwoven sarees and place your first royal order!
            </p>
            <button
              onClick={() => navigate("/collections")}
              className="track-submit-btn"
              style={{ margin: "0 auto" }}
            >
              Explore Saree Collections →
            </button>
          </div>
        )}

        {/* MULTIPLE ORDERS SELECTOR TABS */}
        {ordersList.length > 1 && (
          <div className="orders-tabs-wrapper">
            <div className="orders-tabs-label">
              <span>
                {searchType === "account"
                  ? `Your Account Orders (${ordersList.length})`
                  : searchType === "phone" || searchQuery.replace(/\D/g, "").length === 10
                  ? `Found ${ordersList.length} Orders for Phone: ${searchQuery}`
                  : `Found ${ordersList.length} Orders matching: ${searchQuery}`}
              </span>
              <span style={{ fontSize: "11px", fontWeight: "normal", color: "#777" }}>
                Select an order to inspect details
              </span>
            </div>
            <div className="orders-tabs-list">
              {ordersList.map((ord) => {
                const isSelected = selectedOrder?.id === ord.id;
                return (
                  <div
                    key={ord.id}
                    className={`order-tab-card ${isSelected ? "active" : ""}`}
                    onClick={() => setSelectedOrder(ord)}
                  >
                    <div className="order-tab-id">Order #{formatOrderCode(ord)}</div>
                    <div className="order-tab-meta">
                      {ord.created_at ? new Date(ord.created_at).toLocaleDateString() : "-"} • ₹{parseFloat(ord.total_amount || 0).toLocaleString()}
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: "11px",
                        fontWeight: 700,
                        color:
                          ord.order_status === "delivered"
                            ? "#0d652d"
                            : ord.order_status === "shipped"
                            ? "#b06000"
                            : "#633878",
                        textTransform: "capitalize",
                      }}
                    >
                      ● {ord.order_status || "Pending"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* SELECTED ORDER DETAILS */}
        {selectedOrder && (
          <div className="order-detail-card">
            {/* HEADER */}
            <div className="order-header-row">
              <div>
                <h2 className="order-header-id">Order #{formatOrderCode(selectedOrder)}</h2>
                <div className="order-header-date">
                  Placed on {selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString() : "-"}
                </div>
              </div>

              <div>
                <span className={`status-pill ${selectedOrder.order_status || "pending"}`}>
                  ● {selectedOrder.order_status || "Processing"}
                </span>
              </div>
            </div>

            {/* STEPPER TIMELINE */}
            {(() => {
              const currentStep = getStepStatus(selectedOrder.order_status);
              const fillPercentage =
                currentStep <= 0 ? 10 : Math.min(100, (currentStep / 4) * 100);

              const steps = [
                { label: "Order Placed", icon: ShoppingBag },
                { label: "Confirmed", icon: ShieldCheck },
                { label: "Packed", icon: Package },
                { label: "Shipped", icon: Truck },
                { label: "Delivered", icon: CheckCircle },
              ];

              return (
                <div className="tracking-stepper">
                  <div className="stepper-track-bg">
                    <div
                      className="stepper-track-fill"
                      style={{ width: `${fillPercentage}%` }}
                    />
                  </div>

                  <div className="stepper-steps">
                    {steps.map((st, idx) => {
                      const isCompleted = currentStep > idx;
                      const isActive = currentStep === idx;
                      const IconComponent = st.icon;

                      return (
                        <div
                          key={st.label}
                          className={`step-node ${
                            isCompleted ? "completed" : isActive ? "active" : ""
                          }`}
                        >
                          <div className="step-circle">
                            <IconComponent size={20} />
                          </div>
                          <div className="step-label">{st.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* COURIER DETAILS BANNER (IF SHIPPED) */}
            {(selectedOrder.courier_name || selectedOrder.tracking_number) && (
              <div className="courier-info-card">
                <div className="courier-info-left">
                  <h4>
                    <Truck size={20} /> Courier Shipment Details
                  </h4>
                  <div className="courier-info-details">
                    Partner: <strong>{selectedOrder.courier_name || "Dispatched via Express Courier"}</strong>
                    {selectedOrder.tracking_number && (
                      <>
                        {" "}• AWB / Tracking:{" "}
                        <span
                          className="awb-badge"
                          onClick={() => handleCopyTracking(selectedOrder.tracking_number)}
                          title="Click to copy"
                        >
                          {selectedOrder.tracking_number}
                          <Copy size={12} />
                        </span>
                        {copiedTracking && (
                          <span style={{ fontSize: "11px", color: "#137333", fontWeight: 600 }}>
                            Copied!
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {selectedOrder.tracking_url && (
                  <a
                    href={selectedOrder.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="courier-btn"
                  >
                    Track on Courier Website
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            )}

            {/* INFO GRID: ADDRESS & PAYMENT */}
            <div className="order-info-grid">
              {/* Delivery Address */}
              <div className="info-card">
                <div className="info-card-title">
                  <MapPin size={15} /> Delivery Address
                </div>
                <div className="info-card-text">
                  <strong>{selectedOrder.full_name}</strong>
                  <br />
                  {selectedOrder.address}
                  <br />
                  {selectedOrder.city}, {selectedOrder.state} - {selectedOrder.pin_code}
                  <br />
                  Phone: <strong>{selectedOrder.phone}</strong>
                  {selectedOrder.alt_phone && (
                    <>
                      <br />
                      Alt Phone: <strong>{selectedOrder.alt_phone}</strong>
                    </>
                  )}
                  {selectedOrder.email && (
                    <>
                      <br />
                      Email: {selectedOrder.email}
                    </>
                  )}
                </div>
              </div>

              {/* Payment Details */}
              <div className="info-card">
                <div className="info-card-title">
                  <ShieldCheck size={15} /> Payment Overview
                </div>
                <div className="info-card-text">
                  Payment Method:{" "}
                  <strong style={{ textTransform: "uppercase" }}>
                    {selectedOrder.payment_method === "razorpay"
                      ? "⚡ Razorpay Online (Instant Paid)"
                      : selectedOrder.payment_method === "cod"
                      ? "Cash On Delivery (COD)"
                      : selectedOrder.payment_method || "Online"}
                  </strong>
                  <br />
                  Payment Status:{" "}
                  <span
                    style={{
                      fontWeight: 700,
                      color:
                        selectedOrder.payment_status === "paid" ||
                        selectedOrder.payment_status === "verified"
                          ? "#137333"
                          : selectedOrder.payment_status === "rejected"
                          ? "#c53030"
                          : "#856404",
                      textTransform: "capitalize",
                    }}
                  >
                    ● {selectedOrder.payment_status || "Pending"}
                  </span>
                  {selectedOrder.razorpay_payment_id && (
                    <div style={{ marginTop: 4, fontSize: "12px", color: "#555", fontFamily: "monospace" }}>
                      Transaction ID: {selectedOrder.razorpay_payment_id}
                    </div>
                  )}
                  {selectedOrder.payment_method === "cod" && (
                    <div style={{ marginTop: 6, fontSize: "12px", color: "#666" }}>
                      * Pay cash to delivery partner upon parcel arrival.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ITEMS LIST */}
            <div className="items-section-title">Consignment Items</div>
            {Array.isArray(selectedOrder.items) && selectedOrder.items.length > 0 ? (
              <div className="items-list">
                {selectedOrder.items.map((item, idx) => {
                  const img =
                    item.img_url ||
                    item.image ||
                    item.imageUrl ||
                    item.mainImage ||
                    (item.variant && (item.variant.img_url || item.variant.mainImage)) ||
                    "";
                  const price = Number(item.price || item.variant?.price || 0);
                  const qty = Number(item.quantity || 1);

                  return (
                    <div key={item.product_id || idx} className="item-row">
                      <div className="item-main">
                        {img && <img src={img} alt={item.name} className="item-thumb" />}
                        <div>
                          <div className="item-name">{item.name}</div>
                          <div className="item-variant">
                            {item.variant?.colour && `Colour: ${item.variant.colour}`}
                            {item.variant?.size && ` • Size: ${item.variant.size}`}
                            {item.category && ` • ${item.category}`}
                          </div>
                        </div>
                      </div>

                      <div className="item-price-block">
                        <div className="item-price">₹{(price * qty).toLocaleString()}</div>
                        <div className="item-qty">Qty: {qty}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ color: "#888", fontStyle: "italic" }}>Items information unavailable.</p>
            )}

            {/* PRICING BREAKDOWN */}
            <div className="pricing-summary">
              <div className="summary-line">
                <span>Subtotal</span>
                <span>
                  ₹
                  {Array.isArray(selectedOrder.items)
                    ? selectedOrder.items
                        .reduce((acc, it) => acc + Number(it.price || 0) * Number(it.quantity || 1), 0)
                        .toLocaleString()
                    : parseFloat(selectedOrder.total_amount || 0).toLocaleString()}
                </span>
              </div>

              {Number(selectedOrder.discount_amount || 0) > 0 && (
                <div className="summary-line" style={{ color: "#059669" }}>
                  <span>Discount</span>
                  <span>-₹{Number(selectedOrder.discount_amount).toLocaleString()}</span>
                </div>
              )}

              <div className="summary-line">
                <span>Shipping Charges</span>
                <span>
                  {Number(selectedOrder.shipping_fee || 0) === 0 ? (
                    <strong style={{ color: "#059669" }}>FREE</strong>
                  ) : (
                    `₹${Number(selectedOrder.shipping_fee).toLocaleString()}`
                  )}
                </span>
              </div>

              <div className="summary-line total">
                <span>Total Amount</span>
                <span>₹{parseFloat(selectedOrder.total_amount || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrderPage;
